import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { MessageCircle, X, Check, CheckCheck, Send, Plus, ArrowLeft, AlertOctagon } from "lucide-react";
import { AttachmentPicker, AttachmentGrid } from "@/components/AttachmentPicker";

// Fix C (2026-08-28): burbuja de comunicación operativa con acuse de lectura
// obligatorio. Canal APARTE del chat por objetivo (Chat.jsx) — no se toca.
const PRIORITY_META = {
  alta: { label: "Alta", color: "#BF3437" },
  media: { label: "Media", color: "#f59e0b" },
  baja: { label: "Baja", color: "#16a34a" },
};
const STATUS_META = {
  pendiente: { label: "Pendiente", badge: "gray" },
  leido: { label: "Leído · falta resolución", badge: "amber" },
  resuelto: { label: "Resuelto", badge: "green" },
};
const KIND_LABEL = { novedad: "Novedad", solicitud: "Solicitud", mensaje: "Mensaje directo" };

/** Burbuja flotante top-right, montada en AdminLayout/SupervisorLayout/MobileLayout.
 * NO se monta para cliente/cliente_admin/inquilino (esos usan otros layouts). */
export function OperationalInbox() {
  const { user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "supervisor";
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("pendientes");
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const [resolutionText, setResolutionText] = useState("");
  const [busy, setBusy] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [roster, setRoster] = useState([]);
  const [clients, setClients] = useState([]);
  const [newForm, setNewForm] = useState({ title: "", body: "", assigned_to: [], priority: "media", client_id: "" });
  const [replyText, setReplyText] = useState("");
  const [replyAttachment, setReplyAttachment] = useState(null);
  const lastCount = useRef(0);
  const firstTick = useRef(true);

  const pollCount = async () => {
    try {
      const { data } = await api.get("/inbox/count");
      const c = data.count || 0;
      if (!firstTick.current && c > lastCount.current) toast.info(`Nuevo pendiente en la bandeja (${c} en total)`);
      lastCount.current = c; firstTick.current = false;
      setCount(c);
    } catch { /* silent */ }
  };
  useEffect(() => { pollCount(); const i = setInterval(pollCount, 15000); return () => clearInterval(i); }, []);

  const loadList = async (t) => {
    setList(null);
    try {
      const params = t === "enviados" ? { scope: "created" } : t === "historial" ? { status: "resuelto" } : {};
      const { data } = await api.get("/inbox", { params });
      setList(data);
    } catch (e) { toast.error(formatApiError(e)); setList([]); }
  };

  useEffect(() => { if (open) { loadList(tab); setSelected(null); setResolutionText(""); } }, [open, tab]);
  useEffect(() => { setReplyText(""); setReplyAttachment(null); }, [selected?.id]);

  const openBubble = () => setOpen((o) => !o);

  const openNewMessage = async () => {
    setNewOpen(true);
    try {
      const [r, c] = await Promise.all([api.get("/users/roster"), api.get("/clients")]);
      setRoster(r.data.filter((u) => !["cliente", "cliente_admin", "inquilino"].includes(u.role)));
      setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleAssignee = (uid) => setNewForm((f) => ({
    ...f, assigned_to: f.assigned_to.includes(uid) ? f.assigned_to.filter((x) => x !== uid) : [...f.assigned_to, uid],
  }));

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newForm.title || !newForm.body || newForm.assigned_to.length === 0) return toast.error("Completá destinatario(s), título y mensaje");
    setBusy(true);
    try {
      await api.post("/inbox", { ...newForm, client_id: newForm.client_id || null });
      toast.success(newForm.assigned_to.length > 1 ? `Mensaje enviado a ${newForm.assigned_to.length} destinatarios` : "Mensaje enviado");
      setNewOpen(false); setNewForm({ title: "", body: "", assigned_to: [], priority: "media", client_id: "" });
      pollCount(); if (open) loadList(tab);
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const confirmRead = async (m) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/${m.id}/read`);
      setSelected(data);
      setList((prev) => (prev ? prev.map((x) => (x.id === data.id ? data : x)) : prev));
      pollCount();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitResolve = async (m) => {
    if (!resolutionText.trim()) return toast.error("La resolución no puede estar vacía");
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/${m.id}/resolve`, { resolution: resolutionText });
      toast.success("Mensaje resuelto");
      setSelected(data); setResolutionText("");
      loadList(tab); pollCount();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const isMine = (m) => m.assigned_to === user?.id;
  const isCreator = (m) => m.created_by === user?.id;
  const canReply = (m) => isMine(m) || isCreator(m);

  // Fix P (2026-09-09): la bandeja se vuelve conversacional. Responder NO
  // equivale a resolver -- esa regla sigue intacta y separada.
  const sendReply = async (m) => {
    if (!replyText.trim() && !replyAttachment) return toast.error("Escribí un mensaje o adjuntá una foto");
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/${m.id}/reply`, { body: replyText.trim(), attachment: replyAttachment });
      setSelected(data);
      setList((prev) => (prev ? prev.map((x) => (x.id === data.id ? data : x)) : prev));
      setReplyText(""); setReplyAttachment(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={openBubble}
        style={{
          position: "fixed", top: 64, right: 16, zIndex: 200,
          width: 52, height: 52, borderRadius: "50%", background: "#0A0A0A",
          color: "#FFFBF4", border: "2px solid #BF3437", display: "flex",
          alignItems: "center", justifyContent: "center", cursor: "pointer",
          boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        }}
        data-testid="inbox-bubble-toggle"
        aria-label={`Pendientes de resolución (${count})`}
        title={`Pendientes de resolución (${count}) — leído no es lo mismo que resuelto`}
      >
        <MessageCircle size={22} />
        {count > 0 && (
          <span data-testid="inbox-badge-count" style={{
            position: "absolute", top: -4, right: -4, background: "#BF3437", color: "#fff",
            borderRadius: 10, padding: "1px 6px", fontSize: 11, fontWeight: 800, border: "2px solid #FFFBF4",
          }}>{count}</span>
        )}
      </button>

      {open && (
        <div style={{
          position: "fixed", top: 124, right: 16, zIndex: 199, width: 380, maxWidth: "92vw",
          maxHeight: "66vh", background: "#FFFBF4", borderRadius: 6, boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
          display: "flex", flexDirection: "column", overflow: "hidden", border: "1px solid var(--invict-bone-3)",
        }} data-testid="inbox-panel">
          <div style={{ background: "#0A0A0A", color: "#FFFBF4", padding: "12px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 13, letterSpacing: "0.04em" }}>Bandeja operativa</div>
              {/* Fix W (2026-09-11): el badge cuenta PENDIENTES DE RESOLUCIÓN (leído
                  no lo saca de la lista, hace falta cargar la resolución) -- se
                  aclara acá para que no se confunda con "no leído". */}
              <div style={{ fontSize: 10, color: "rgba(255,251,244,0.65)", marginTop: 1 }} data-testid="inbox-panel-subtitle">Pendientes de resolución ({count})</div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {canAssign && <button onClick={openNewMessage} className="btn-ghost" style={{ padding: "4px 8px", fontSize: 10, color: "#FFFBF4", borderColor: "rgba(255,251,244,0.3)" }} data-testid="inbox-new-message-btn"><Plus size={12} style={{ marginRight: 3 }} />Nuevo</button>}
              <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "#FFFBF4", cursor: "pointer" }} data-testid="inbox-close"><X size={18} /></button>
            </div>
          </div>

          {!selected && (
            <div style={{ display: "flex", borderBottom: "1px solid var(--invict-bone-3)" }}>
              <button onClick={() => setTab("pendientes")} className={tab === "pendientes" ? "" : ""} style={{ flex: 1, padding: "8px 4px", fontSize: 11, fontWeight: 800, background: tab === "pendientes" ? "var(--invict-bone-2)" : "transparent", border: "none", borderBottom: tab === "pendientes" ? "2px solid #BF3437" : "2px solid transparent", cursor: "pointer" }} data-testid="inbox-tab-pendientes">Pendientes</button>
              <button onClick={() => setTab("historial")} style={{ flex: 1, padding: "8px 4px", fontSize: 11, fontWeight: 800, background: tab === "historial" ? "var(--invict-bone-2)" : "transparent", border: "none", borderBottom: tab === "historial" ? "2px solid #BF3437" : "2px solid transparent", cursor: "pointer" }} data-testid="inbox-tab-historial">Historial</button>
              {canAssign && <button onClick={() => setTab("enviados")} style={{ flex: 1, padding: "8px 4px", fontSize: 11, fontWeight: 800, background: tab === "enviados" ? "var(--invict-bone-2)" : "transparent", border: "none", borderBottom: tab === "enviados" ? "2px solid #BF3437" : "2px solid transparent", cursor: "pointer" }} data-testid="inbox-tab-enviados">Enviados</button>}
            </div>
          )}

          <div style={{ flex: 1, overflowY: "auto", padding: selected ? 14 : 0 }}>
            {!selected && list == null && <div style={{ padding: 20 }}><div className="skel" style={{ height: 60 }} /></div>}
            {!selected && list && list.length === 0 && <div style={{ padding: 30, textAlign: "center", fontSize: 12, color: "var(--invict-text-muted)" }} data-testid="inbox-empty">Sin mensajes.</div>}
            {!selected && list && list.map((m) => {
              // Fix W (2026-09-11): distinguir en la lista un pendiente YA
              // LEÍDO (esperando resolución) de uno que todavía no se abrió.
              const awaitingResolution = tab === "pendientes" && m.status === "leido" && isMine(m);
              return (
              <button key={m.id} onClick={() => setSelected(m)} style={{ width: "100%", textAlign: "left", padding: "10px 14px", borderBottom: "1px solid var(--invict-bone-3)", borderLeft: awaitingResolution ? "3px solid #d97706" : "3px solid transparent", background: awaitingResolution ? "rgba(217,119,6,0.06)" : "none", border: "none", borderBottomStyle: "solid", cursor: "pointer" }} data-testid={`inbox-item-${m.id}`}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#0A0A0A" }}>{m.title}</div>
                  <span style={{ fontSize: 9, fontWeight: 800, color: PRIORITY_META[m.priority]?.color, textTransform: "uppercase" }}>{PRIORITY_META[m.priority]?.label}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 3 }}>{KIND_LABEL[m.kind]} · {m.client_name || "Sin objetivo"} {tab === "enviados" ? `· para ${m.assigned_to_name}` : `· de ${m.created_by_name}`}</div>
                <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 8 }}>
                  <span className={`badge ${STATUS_META[m.status]?.badge}`} style={{ fontSize: 9 }}>{STATUS_META[m.status]?.label}</span>
                  {awaitingResolution && <span style={{ fontSize: 10, fontWeight: 700, color: "#d97706" }} data-testid={`inbox-item-needs-resolution-${m.id}`}>Falta cargar la resolución para cerrarlo →</span>}
                </div>
              </button>
              );
            })}

            {selected && (
              <div data-testid="inbox-item-detail">
                <button onClick={() => setSelected(null)} className="btn-ghost" style={{ padding: "3px 8px", fontSize: 10, marginBottom: 10 }} data-testid="inbox-detail-back"><ArrowLeft size={11} style={{ marginRight: 4 }} />Volver</button>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                  <h4 style={{ margin: 0, fontSize: 14 }}>{selected.title}</h4>
                  <span className={`badge ${STATUS_META[selected.status]?.badge}`}>{STATUS_META[selected.status]?.label}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", margin: "6px 0" }}>
                  {KIND_LABEL[selected.kind]} · {selected.client_name || "Sin objetivo"} · de {selected.created_by_name} · para {selected.assigned_to_name}
                </div>

                {/* Fix P (2026-09-09): hilo de conversación estilo chat -- mensaje original + respuestas en orden cronológico. */}
                <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }} data-testid="inbox-thread">
                  <ChatBubble authorName={selected.created_by_name} mine={isCreator(selected)} at={selected.created_at} body={selected.body} />
                  {(selected.replies || []).map((r) => (
                    <ChatBubble key={r.id} authorName={r.author_name} mine={r.author_id === user?.id} at={r.at} body={r.body} attachment={r.attachment} />
                  ))}
                </div>

                {canReply(selected) && selected.status !== "resuelto" && (
                  <div style={{ marginBottom: 14, paddingTop: 10, borderTop: "1px dashed var(--invict-bone-3)" }} data-testid="inbox-reply-composer">
                    <div className="field" style={{ marginBottom: 6 }}>
                      <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Escribí una respuesta..." rows={2} data-testid="inbox-reply-input" />
                    </div>
                    <AttachmentPicker accept="image" multiple={false} max={1} label="Foto"
                      value={replyAttachment ? [replyAttachment] : []}
                      onChange={(list) => setReplyAttachment(list[list.length - 1] || null)} />
                    <button onClick={() => sendReply(selected)} disabled={busy} className="btn-ghost" style={{ width: "100%", marginTop: 8 }} data-testid="inbox-reply-send">
                      <Send size={12} style={{ marginRight: 6 }} />Enviar respuesta
                    </button>
                  </div>
                )}

                {isMine(selected) && selected.status !== "resuelto" && (
                  <div>
                    {!selected.read_at ? (
                      <button onClick={() => confirmRead(selected)} disabled={busy} className="btn-primary" style={{ width: "100%" }} data-testid="inbox-confirm-read"><Check size={13} style={{ marginRight: 6 }} />Confirmar lectura</button>
                    ) : (
                      <div>
                        <div style={{ fontSize: 11, color: "#16a34a", marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}><CheckCheck size={12} />Leído el {new Date(selected.read_at).toLocaleString("es-AR")}</div>
                        <div className="field"><label>Resolución *</label>
                          <textarea value={resolutionText} onChange={(e) => setResolutionText(e.target.value)} placeholder="Contá qué se hizo para resolverlo..." data-testid="inbox-resolution-input" />
                        </div>
                        <button onClick={() => submitResolve(selected)} disabled={busy} className="btn-primary" style={{ width: "100%" }} data-testid="inbox-resolve-submit"><Send size={13} style={{ marginRight: 6 }} />Cargar resolución y cerrar</button>
                      </div>
                    )}
                  </div>
                )}

                {!isMine(selected) && (
                  <div style={{ fontSize: 12 }}>
                    {selected.read_at ? (
                      <div style={{ color: "#16a34a", marginBottom: 6 }}><CheckCheck size={12} style={{ marginRight: 4, display: "inline" }} />Leído por {selected.assigned_to_name} el {new Date(selected.read_at).toLocaleString("es-AR")}</div>
                    ) : (
                      <div style={{ color: "var(--invict-text-muted)" }}>Todavía no fue leído por {selected.assigned_to_name}.</div>
                    )}
                    {selected.resolution && (
                      <div style={{ marginTop: 8, background: "rgba(22,163,74,0.08)", borderLeft: "3px solid #16a34a", padding: 10 }}>
                        <b>Resolución de {selected.resolved_by_name}:</b>
                        <div style={{ marginTop: 4, fontStyle: "italic" }}>"{selected.resolution}"</div>
                        <div style={{ fontSize: 10, color: "var(--invict-text-muted)", marginTop: 4 }}>{new Date(selected.resolved_at).toLocaleString("es-AR")}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {newOpen && (
        <div className="modal-overlay" onClick={() => setNewOpen(false)}>
          <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submitNew} data-testid="inbox-new-modal">
            <h3><AlertOctagon size={15} style={{ marginRight: 6, display: "inline", color: "#BF3437" }} />Nuevo mensaje directo</h3>
            <p>Queda en la bandeja del destinatario hasta que confirme lectura y cargue una resolución.</p>
            <div className="field"><label>Destinatarios *</label>
              <div style={{ maxHeight: 160, overflowY: "auto", border: "1px solid var(--invict-bone-3)", borderRadius: 4, padding: 6 }} data-testid="inbox-new-assignee-list">
                {roster.map((u) => (
                  <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 4px", fontSize: 12, cursor: "pointer" }} data-testid={`inbox-new-assignee-${u.id}`}>
                    <input type="checkbox" checked={newForm.assigned_to.includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                    {u.name} ({u.role})
                  </label>
                ))}
              </div>
              {newForm.assigned_to.length > 0 && (
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 4 }} data-testid="inbox-new-assignee-count">{newForm.assigned_to.length} destinatario(s) seleccionado(s)</div>
              )}
            </div>
            <div className="field"><label>Objetivo (opcional)</label>
              <select value={newForm.client_id} onChange={(e) => setNewForm({ ...newForm, client_id: e.target.value })} data-testid="inbox-new-client">
                <option value="">Sin objetivo específico</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="field"><label>Prioridad</label>
              <select value={newForm.priority} onChange={(e) => setNewForm({ ...newForm, priority: e.target.value })} data-testid="inbox-new-priority">
                <option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option>
              </select>
            </div>
            <div className="field"><label>Título *</label>
              <input value={newForm.title} onChange={(e) => setNewForm({ ...newForm, title: e.target.value })} placeholder="Ej: Revisar cámara del portón sur" data-testid="inbox-new-title" />
            </div>
            <div className="field"><label>Mensaje *</label>
              <textarea value={newForm.body} onChange={(e) => setNewForm({ ...newForm, body: e.target.value })} placeholder="Detalle del pedido..." data-testid="inbox-new-body" />
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button type="button" className="btn-ghost" onClick={() => setNewOpen(false)}>Cancelar</button>
              <button type="submit" className="btn-primary" disabled={busy} data-testid="inbox-new-submit">Enviar</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

function ChatBubble({ authorName, mine, at, body, attachment }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: mine ? "flex-end" : "flex-start" }} data-testid="inbox-chat-bubble">
      <div style={{
        maxWidth: "85%", padding: "8px 10px", borderRadius: 10,
        background: mine ? "#0A0A0A" : "var(--invict-bone-2)", color: mine ? "#FFFBF4" : "#0A0A0A",
      }}>
        <div style={{ fontSize: 10, fontWeight: 800, opacity: 0.7, marginBottom: 3 }}>{authorName}</div>
        {body && <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{body}</div>}
        {attachment && <div style={{ marginTop: 4, maxWidth: 140 }}><AttachmentGrid items={[attachment]} /></div>}
      </div>
      <div style={{ fontSize: 9, color: "var(--invict-text-muted)", marginTop: 2 }}>{new Date(at).toLocaleString("es-AR")}</div>
    </div>
  );
}

